/**
 * Series-end evaluation — when (if ever) a recurring series stops producing
 * occurrences. Replaces two hand-rolled readers: the server published-feed
 * filter's `rruleUntilMs` (which read a DATE-only UNTIL as 00:00 UTC while
 * the client read 23:59:59 UTC) and ImportDialog's frequency-arithmetic
 * COUNT estimate.
 */

import { RRule } from 'rrule';
import { parseRecurrenceText } from './text';
import { rawUntilToken, untilTokenToMs } from './until';
import { hasFixedOffset, fromFloating, toFloating } from './zone';

/**
 * The series' UNTIL as an instant under the shared UNTIL policy — cheap
 * (token read, no library iteration). Null when there is no UNTIL or the
 * value is unreadable.
 */
export function seriesUntilMs(recurrenceText: string | null, zone?: string | null): number | null {
  const parsed = parseRecurrenceText(recurrenceText);
  if (!parsed) return null;
  const token = rawUntilToken(parsed.rrule);
  if (!token) return null;
  return untilTokenToMs(token, zone ?? 'UTC');
}

export interface SeriesEndOptions {
  startIso: string | Date;
  /** The series' effective anchor zone (`rruleTimezone ?? timezone`). */
  zone?: string | null;
}

/**
 * The instant of the series' LAST occurrence start, or null when the series
 * never ends (no UNTIL and no COUNT) — or when it could not be safely
 * evaluated (the conservative reading for every consumer so far: an
 * import-preselection or a keep-if-live filter treats "unknown" as "may
 * still produce occurrences").
 *
 * COUNT rules are evaluated from their TRUE start — never re-anchored, so a
 * historically exhausted COUNT=5 series stays exhausted — under the patched
 * iterator's advance budget; exceeding the budget returns null (unknown).
 */
export function seriesEndMs(
  recurrenceText: string | null,
  options: SeriesEndOptions,
): number | null {
  const parsed = parseRecurrenceText(recurrenceText);
  if (!parsed) return null;

  const untilToken = rawUntilToken(parsed.rrule);
  const zone = options.zone ?? 'UTC';
  if (untilToken) {
    // The UNTIL bounds the series; the policy instant is the end bound.
    return untilTokenToMs(untilToken, zone);
  }

  let ruleOptions;
  try {
    ruleOptions = RRule.parseString(parsed.rrule);
  } catch {
    return null;
  }
  if (!ruleOptions.count) return null; // never ends

  const start = new Date(options.startIso);
  if (Number.isNaN(start.getTime())) return null;
  const zoned = !hasFixedOffset(zone);
  const dtstart = zoned ? toFloating(start, zone) : start;

  try {
    const rule = new RRule({ ...ruleOptions, dtstart });
    const all = rule.all();
    if (all.length === 0) return null;
    const last = all[all.length - 1];
    return zoned ? fromFloating(last, zone).getTime() : last.getTime();
  } catch {
    // RRULE_ITERATION_LIMIT or construction failure: unknown, not "ended".
    return null;
  }
}
